import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { translate } from 'components/Translation';
import moment, { Moment } from 'moment';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { finalize } from 'rxjs/operators';
import { ActivitiesService } from 'services/activities/activities';
import { SingleCorpAccount } from 'utils/api/models/accounts/singleCorpAccount';
import AccountFilterTable, { ISelectedAccount } from 'views/common/AccountFilterTable';
import { SingleActivity } from 'utils/api/models/activities/singleActivity';
import { throwError } from 'rxjs';
import { AccountCell, TextCell } from 'components/Cells/Cells';
import { Column, FilterValue } from 'react-table';
import CustomDatePicker from 'components/CustomDatePicker/CustomDatePicker';
import { now } from 'utils/date/getDate';
import styles from 'assets/jss/material-dashboard-pro-react/views/activityList/activityStyle';
import { makeStyles } from '@material-ui/styles';
import _ from 'lodash';
import ActivityDetailsDialog from '../Details/ActivityDetailsDialog';
import Button from 'components/CustomButtons/Button';
import { useDispatch } from 'react-redux';
import { AccountsService } from 'services/accounts/accounts';
import CustomAvatar from 'components/CustomAvatar/CustomAvatar';
import Business from '@material-ui/icons/Business';
import { EVENT_CODES } from '../constants';

const useStyles = makeStyles(styles);

interface IPeriod {
	startDate: Moment | null;
	endDate: Moment | null;
}

const LAST_DAYS_NUMBER = 30;
const INTERACTIONS_SELFCARE_CHANNEL = 'SELFCARE';

const ListOfActivities = () => {
	const [accounts, setAccounts] = useState<SingleCorpAccount[]>([]);
	const [selectedAccount, setSelectedAccount] = useState<ISelectedAccount | null>(null);
	const [searchInputValue, setSearchInputValue] = useState<string>('');
	const [selected, setSelected] = useState<number[]>([]);
	const [filteredData, setFilteredData] = useState<SingleActivity[]>([]);
	const [activitiesList, setActivitiesList] = useState<SingleActivity[]>([]);
	const [period, setPeriod] = useState<IPeriod>({
		startDate: moment()
			.subtract(LAST_DAYS_NUMBER, 'd')
			.startOf('day'),
		endDate: moment().endOf('day'),
	});
	const [filters, setFilters] = useState<FilterValue>({
		period: ['lastDays'],
		performedBy: [],
		activity: [],
		accountId: [],
		businessId: [],
	});
	const [pageSize, setPageSize] = useState<number>(10);
	const [skip, setSkip] = useState<number>(0);
	const [currentPage, setCurrentPage] = useState<number>(0);
	const [interactionId, setInteractionId] = useState<number | null>(null);
	const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
	const [isAccountsFetched, setIsAccountsFetched] = useState<boolean>(false);

	const limit = 100;

	const classes = useStyles();

	const dispatch = useDispatch();

	const interactionsRequestData = useMemo(() => {
		return {
			limit: limit,
			skip: skip,
			dateFrom: period.startDate ? period.startDate.format('YYYY-MM-DD HH:mm:ss').toString() : null,
			dateTo: period.endDate ? period.endDate.format('YYYY-MM-DD HH:mm:ss').toString() : null,
			accountId: null,
			eventCode: null,
			channel: INTERACTIONS_SELFCARE_CHANNEL,
			upId: selectedAccount?.id || null,
		};
	}, [skip, limit, period, selectedAccount]);

	const exportCSV = () => {
		let interactionIds: number[] = [];

		if (
			filters?.performedBy?.length ||
			filters?.activity?.length ||
			filters?.accountId?.length ||
			filters?.businessId?.length
		) {
			interactionIds = filteredData.map((activity) => activity.interactionId);
		}

		if (selected?.length) {
			interactionIds = filteredData
				.filter((_, index) => selected.indexOf(index) > -1)
				.map((activity) => activity.interactionId);
		}

		dispatch(showLoadingIndicator());
		return ActivitiesService.getInteractionsCsv({ ...interactionsRequestData, interactionIds })
			.pipe(
				finalize(() => {
					dispatch(hideLoadingIndicator());
				})
			)
			.subscribe(
				(response: any) => {
					if (response?.response) {
						try {
							const href = window.URL.createObjectURL(response.response);
							const a = document.createElement('a');
							a.style.display = 'none';
							a.href = href;
							a.download = `list of activities ${moment().format('LLL')}}`;
							document.body.appendChild(a);
							a.click();
							window.URL.revokeObjectURL(href);
						} catch (error) {}
					}
				},
				(error) => throwError(error)
			);
	};

	const fetchActivities = useCallback(() => {
		if (!isAccountsFetched) {
			return;
		}
		if (accounts.length === 1) {
			interactionsRequestData.upId = null;
		}
		dispatch(showLoadingIndicator());
		return ActivitiesService.getInteractions(interactionsRequestData)
			.pipe(
				finalize(() => {
					dispatch(hideLoadingIndicator());
				})
			)
			.subscribe(
				({ activities }) => {
					if (skip === 0) {
						setActivitiesList(activities);
					} else {
						setActivitiesList((_activities) => [..._activities, ...activities]);
					}
				},
				(error) => throwError(error)
			);
	}, [accounts.length, dispatch, interactionsRequestData, skip, isAccountsFetched]);

	useEffect(() => {
		const request = fetchActivities();
		return () => {
			dispatch(hideLoadingIndicator());
			if (request) request.unsubscribe();
		};
	}, [dispatch, fetchActivities]);

	useEffect(() => {
		const subscription = AccountsService.getCorpAccountsForCurrentUser().subscribe(({ accounts }) => {
			setAccounts(accounts);
			setIsAccountsFetched(true);
		});
		return () => {
			subscription.unsubscribe();
		};
	}, []);

	useEffect(() => {
		setSkip(0);
	}, [period, selectedAccount]);

	useEffect(() => {
		setSelected([]);
		setFilteredData([...activitiesList]);
	}, [selectedAccount, activitiesList]);

	const filter = useCallback(
		_.debounce(() => {
			const _searchInputValue = searchInputValue.toLowerCase();
			const filtered = activitiesList.filter((activity) => {
				return [
					[
						filters.performedBy.length > 0,
						filters.performedBy.some((performedBy: string) => activity.performedBy === performedBy),
					],
					[filters.activity.length > 0, filters.activity.some((act: string) => act === activity.eventCode)],
					[
						filters.accountId.length > 0,
						filters.accountId.some(
							(accountId: string) =>
								(activity.accountId ? activity.accountId.toString() : '') === accountId
						),
					],
					[
						filters.businessId.length > 0,
						filters.businessId.some((businessId: string) => businessId === activity.businessId),
					],
					[
						_searchInputValue !== '',
						['performedBy', 'eventCode', 'accountId', 'businessId']
							.filter((field) => !!activity[field])
							.some((field) =>
								activity[field]
									.toString()
									.toLowerCase()
									.includes(_searchInputValue)
							),
					],
				]
					.filter((check) => check[0])
					.every((check) => check[1]);
			});
			setFilteredData(filtered);
		}, 100),
		[searchInputValue, filters, selectedAccount, activitiesList]
	);

	useEffect(() => {
		filter();
	}, [filter]);

	const accountsMap = useMemo(
		() => accounts.reduce((acc, account) => ({ ...acc, [account.accountId]: account }), {}),
		[accounts]
	);

	const hideDialog = () => {
		setIsDialogOpen(false);
		setInteractionId(null);
	};

	const validateDateString = (date: string): boolean => {
		return /^([0][1-9]|[12][0-9]|3[01])[/](0?[1-9]|1[012])[/]\d{4}$/.test(date);
	};

	const dateChanged = (date: string | moment.Moment, key: 'startDate' | 'endDate'): void => {
		if (typeof date === 'string') {
			if (validateDateString(date)) {
				const updatedDate = moment(date);
				if (updatedDate.isValid()) {
					setPeriod((period) => ({ ...period, [key]: updatedDate }));
				}
			}
		} else {
			setPeriod((period) => ({ ...period, [key]: date }));
		}
	};

	const prepareDate = (value: any[]) => {
		if (!value.length) {
			setPeriod({ startDate: null, endDate: moment().endOf('day') });
		} else if (value[0] === 'lastDays') {
			setPeriod({
				startDate: moment()
					.subtract(LAST_DAYS_NUMBER, 'd')
					.startOf('day'),
				endDate: moment().endOf('day'),
			});
		}
	};

	const checkIfANumber = (value: string) => {
		const regExp = /[a-zA-Z]/g;
		if (regExp.test(value)) {
			return false;
		}
		return true;
	};
	const accountIdFilters = useMemo(
		() => [...new Set(activitiesList.map((activity) => (activity.accountId ? activity.accountId.toString() : '')))],
		[activitiesList]
	);

	return (
		<div style={{ width: '100%' }}>
			<AccountFilterTable
				onPaggingChange={(event: any) => {
					setCurrentPage(event);
					if ((event + 1) * pageSize >= activitiesList.length) {
						setSkip(activitiesList.length);
					}
				}}
				onPageSizeChange={(event: any) => {
					setPageSize(event);
					if ((currentPage + 1) * event >= activitiesList.length) {
						setSkip(activitiesList.length);
					}
				}}
				accounts={accounts}
				setAccount={(value) => setSelectedAccount(value)}
				selectedAccount={selectedAccount}
				onFilterChange={(name, value) => {
					setFilters((filters: any) => ({ ...filters, [name]: value }));
					if (name === 'period') prepareDate(value);
				}}
				filterHeader={translate('path.list.of.activities')}
				filters={[
					{
						title: translate('payments.bills.period'),
						name: 'period',
						type: 'one',
						elements: [
							{
								title: translate('numbers.usage.customPeriod'),
								value: 'custom',
								show: (
									<>
										<div className={classes.datePicker}>
											<CustomDatePicker
												timeFormat={false}
												value={period.startDate}
												onChange={(date) => dateChanged(date, 'startDate')}
												isValidDate={(currentDate) => currentDate.isSameOrBefore(now)}
												inputProps={{
													placeholder: translate('activities.details.chooseStartDate'),
												}}
											/>
										</div>
										<div className={classes.datePicker}>
											<CustomDatePicker
												timeFormat={false}
												value={period.endDate}
												onChange={(date) => dateChanged(date, 'endDate')}
												isValidDate={(currentDate) => {
													if (period.startDate) {
														return (
															period.startDate?.isBefore(currentDate, 'day') &&
															currentDate.isSameOrBefore(now)
														);
													} else {
														return false;
													}
												}}
												inputProps={{}}
											/>
										</div>
									</>
								),
							},
							{
								title: translate('last.days', LAST_DAYS_NUMBER),
								value: 'lastDays',
							},
						],
					},
					{
						title: translate('table.user.performedBy'),
						name: 'performedBy',
						type: 'multiple',
						elements: [...new Set(activitiesList.map((activity) => activity.performedBy))].map((value) => ({
							title: value,
							value,
						})),
					},
					{
						title: translate('table.activity'),
						name: 'activity',
						type: 'multiple',
						elements: [...new Set(activitiesList.map((activity) => activity.eventCode))].map((value) => ({
							title: value.replace(/_/g, ' '),
							value,
						})),
					},
					{
						title: translate('activities.details.accountId'),
						name: 'accountId',
						type: 'multiple',
						elements: accountIdFilters
							.filter((item) => item !== '')
							.concat(accountIdFilters.filter((item) => item === ''))
							.map((value) => ({
								title: value || translate('activities.no.account.id'),
								value,
							})),
					},
					{
						title: translate('table.businessId'),
						name: 'businessId',
						type: 'multiple',
						elements: [...new Set(activitiesList.map((activity) => activity.businessId))]
							.filter(
								(value) => checkIfANumber(value) || value === translate('activities.no.business.id')
							)
							.map((value) => ({
								title: value,
								value,
							})),
					},
				]}
				filtersValue={filters}
				setSearchInputValue={(value) => setSearchInputValue(value)}
				searchInputValue={searchInputValue}
				exportToCSV={exportCSV}
				data={filteredData}
				onSelect={(value: any) => setSelected(value)}
				selectPanel={{
					selectedElements: selected.length,
					downloadMenuItems: [
						{
							name: 'CSV',
							onClick: exportCSV,
						},
					],
				}}
				loading={false}
				selected={selected}
				columns={
					[
						{
							Header: translate('table.date'),
							// eslint-disable-next-line react/prop-types
							Cell: ({ original }: { original: SingleActivity }) => (
								<TextCell
									text={moment(original.eventDate)
										.format('lll')
										.toString()}
								/>
							),
							accessor: 'eventDate',
						},
						{
							Header: translate('table.user.performedBy'),
							Cell: ({ original }: { original: SingleActivity }) => (
								<TextCell text={original.performedBy} />
							),
							accessor: 'performedBy',
						},
						!selectedAccount?.id && {
							Header: translate('table.organization.name'),
							Cell: ({ original }: { original: SingleActivity }) => {
								const account = accountsMap[original.createdBy];
								return account ? <TextCell text={account.accountName} /> : '-';
							},
							accessor: 'account.accountName',
						},
						{
							Header: translate('table.activity'),
							Cell: ({ original }: { original: SingleActivity }) => (
								<TextCell text={original.eventCode.replace(/_/g, ' ')} />
							),
							accessor: 'eventCode',
						},
						{
							Header: translate('activities.details.accountId'),
							Cell: ({ original }: { original: SingleActivity }) => (
								<TextCell text={original.accountId ? original.accountId : '-'} />
							),
							accessor: 'accountId',
						},
						{
							Header: translate('table.businessId'),
							Cell: ({ original }: { original: SingleActivity }) => (
								<TextCell text={checkIfANumber(original.businessId) ? original.businessId : '-'} />
							),
							accessor: 'businessId',
						},
						{
							Cell: ({ original }: { original: SingleActivity }) => (
								<Button
									color="primary-light"
									onClick={() => {
										setInteractionId(original.interactionId);
										setIsDialogOpen(true);
									}}
									disabled={[EVENT_CODES.PASSWORD_RESET, EVENT_CODES.PROFILE_CREATE].includes(
										original.eventCode
									)}
								>
									{translate('activities.details.activityDetails')}
								</Button>
							),
						},
					].filter(Boolean) as Column<any>[]
				}
				sm={8}
				lg={5}
			/>
			<ActivityDetailsDialog interactionId={interactionId} isOpen={isDialogOpen} turnOffDialog={hideDialog} />
		</div>
	);
};

export default ListOfActivities;
